import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import MainLayout from '../layouts/MainLayout';
import { 
  Plus, Edit, Search, Eye, Upload, X, FileText, Trash2,
  Bold, Italic, Heading1, Heading2, Heading3, List, ListOrdered, Quote,
  Square, AlignCenter, Link, CornerDownLeft
} from 'lucide-react';
import * as blogService from '../services/blogService';
import { createSlug } from '../services/categoryService';

interface BlogPost {
  id?: string;
  _id?: string;
  slug?: string | { tr: string; en: string; es: string }; // Hem eski hem yeni format
  title: {
    tr: string;
    en: string;
    es: string;
  };
  content: {
    tr: string;
    en: string;
    es: string;
  };
  tags: string[];
  status: 'published' | 'draft';
  author: string;
  createdAt?: string;
  updatedAt?: string;
  publishedAt?: string;
  coverImage?: string;
}

const dummyBlogPosts: BlogPost[] = [
  {
    id: '1',
    title: {
      tr: 'Sürdürülebilir Su Yönetimi Teknolojileri',
      en: 'Sustainable Water Management Technologies',
      es: 'Tecnologías de Gestión Sostenible del Agua'
    },
    content: {
      tr: 'Modern su yönetimi sistemlerinde sürdürülebilirlik...',
      en: 'Sustainability in modern water management systems...',
      es: 'Sostenibilidad en los sistemas modernos de gestión del agua...'
    },
    tags: ['su yönetimi', 'sürdürülebilirlik', 'teknoloji'],
    status: 'published',
    author: 'ZPC Editör',
    createdAt: '2024-01-15T10:30:00Z'
  },
  {
    id: '2',
    title: {
      tr: 'PE Boru Sistemlerinin Avantajları',
      en: 'Advantages of PE Pipe Systems',
      es: 'Ventajas de los Sistemas de Tuberías PE'
    },
    content: {
      tr: 'PE boru sistemleri modern altyapı projelerinde...',
      en: 'PE pipe systems in modern infrastructure projects...',
      es: 'Sistemas de tuberías PE en proyectos de infraestructura modernos...'
    },
    tags: ['PE boru', 'altyapı', 'inşaat'],
    status: 'draft',
    author: 'ZPC Editör',
    createdAt: '2024-01-20T14:15:00Z'
  }
];

const Blog: React.FC = () => {
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPost, setEditingPost] = useState<BlogPost | null>(null);
  const [selectedLanguage, setSelectedLanguage] = useState<'tr' | 'en' | 'es'>('tr');
  const [searchTerm, setSearchTerm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<{
    image?: File;
  }>({});
  const [showPreview, setShowPreview] = useState(false);
  const [contentRef, setContentRef] = useState<HTMLTextAreaElement | null>(null);
  const [currentContent, setCurrentContent] = useState('');
  const [currentAuthor, setCurrentAuthor] = useState('');
  const [currentTags, setCurrentTags] = useState<string[]>([]);
  
  // Her dil için ayrı form state'leri
  const [formTitles, setFormTitles] = useState({ tr: '', en: '', es: '' });
  const [formContents, setFormContents] = useState({ tr: '', en: '', es: '' });
  const [formSlugs, setFormSlugs] = useState({ tr: '', en: '', es: '' });

  // Blog yazılarını API'den yükle
  const fetchBlogs = async () => {
    try {
      setLoading(true);
      const blogsData = await blogService.getBlogs();
      console.log('Blogs received:', blogsData);
      setBlogPosts(blogsData);
    } catch (error) {
      console.error('Error fetching blogs:', error);
      toast.error('Blog yazıları yüklenirken hata oluştu');
      setBlogPosts(dummyBlogPosts); // Fallback to dummy data
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlogs();
  }, []);

  // Dil değiştiğinde content'i güncelle
  useEffect(() => {
    if (editingPost) {
      // Yeni dile geç
      setCurrentContent(formContents[selectedLanguage] || '');
    }
  }, [selectedLanguage, editingPost, formContents]);

  // Rich Text Editor Functions
  const insertText = (before: string, after: string = '') => {
    if (!contentRef) return;
    
    const textarea = contentRef;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = textarea.value.substring(start, end);
    
    const newText = before + selectedText + after;
    const newValue = textarea.value.substring(0, start) + newText + textarea.value.substring(end);
    
    // Form state'ini güncelle
    setFormContents(prev => ({
      ...prev,
      [selectedLanguage]: newValue
    }));
    setCurrentContent(newValue);
    
    // Cursor pozisyonunu ayarla
    setTimeout(() => {
      const newCursorPos = start + before.length + selectedText.length + after.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
      textarea.focus();
    }, 0);
  };

  const formatText = (type: string) => {
    switch (type) {
      case 'bold':
        insertText('<strong>', '</strong>');
        break;
      case 'italic':
        insertText('<em>', '</em>');
        break;
      case 'h1':
        insertText('<h1>', '</h1>');
        break;
      case 'h2':
        insertText('<h2>', '</h2>');
        break;
      case 'h3':
        insertText('<h3>', '</h3>');
        break;
      case 'ul':
        insertText('<ul>\n  <li>', '</li>\n</ul>');
        break;
      case 'ol':
        insertText('<ol>\n  <li>', '</li>\n</ol>');
        break;
      case 'blockquote':
        insertText('<blockquote>', '</blockquote>');
        break;
      case 'p':
        insertText('<p>', '</p>');
        break;
      case 'br':
        insertText('<br />');
        break;
      case 'div':
        insertText('<div class="text-center">', '</div>');
        break;
      case 'link':
        insertText('<a href="URL_BURAYA" target="_blank">', '</a>');
        break;
    }
  };

  const handleEdit = (post: BlogPost) => {
    const normalizedPost = {
      ...post,
      id: post.id || post._id,
      title: post.title || { tr: '', en: '', es: '' },
      content: post.content || { tr: '', en: '', es: '' },
      tags: post.tags || [],
      author: post.author || 'Admin',
      status: post.status || 'draft'
    };
    
    setEditingPost(normalizedPost);
    
    // Form state'lerini güncelle
    setFormTitles({
      tr: normalizedPost.title.tr || '',
      en: normalizedPost.title.en || '',
      es: normalizedPost.title.es || ''
    });
    setFormContents({
      tr: normalizedPost.content.tr || '',
      en: normalizedPost.content.en || '',
      es: normalizedPost.content.es || ''
    });
    
    // Slug'ları yükle - API'den gelen slug object veya string olabilir
    if (typeof post.slug === 'object' && post.slug) {
      setFormSlugs({
        tr: post.slug.tr || '',
        en: post.slug.en || '',
        es: post.slug.es || ''
      });
    } else if (typeof post.slug === 'string') {
      // Eski format - tüm dillere aynı slug'ı ata
      setFormSlugs({
        tr: post.slug,
        en: post.slug,
        es: post.slug
      });
    } else {
      setFormSlugs({ tr: '', en: '', es: '' });
    }
    
    // Diğer form alanları
    setCurrentContent(normalizedPost.content.tr || '');
    setCurrentAuthor(normalizedPost.author);
    setCurrentTags(normalizedPost.tags);
    
    // Dili TR'ye ayarla
    setSelectedLanguage('tr');
    
    // Mevcut görsel varsa önizleme olarak ayarla
    if (post.coverImage) {
      setImagePreview(post.coverImage);
    } else {
      setImagePreview(null);
    }
    
    setSelectedFiles({});
    setShowForm(true);
  };

  const handleNewPost = () => {
    setEditingPost(null);
    
    // Form state'lerini temizle
    setFormTitles({ tr: '', en: '', es: '' });
    setFormContents({ tr: '', en: '', es: '' });
    setFormSlugs({ tr: '', en: '', es: '' });
    
    setCurrentContent('');
    setCurrentAuthor('');
    setCurrentTags([]);
    
    // Dili TR'ye ayarla
    setSelectedLanguage('tr');
    
    setImagePreview(null);
    setSelectedFiles({});
    setShowForm(true);
  };

  const handleCloseForm = () => {
    // Blob URL'leri temizle
    if (imagePreview && imagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(imagePreview);
    }
    
    // Form state'lerini temizle
    setFormTitles({ tr: '', en: '', es: '' });
    setFormContents({ tr: '', en: '', es: '' });
    
    setShowForm(false);
    setEditingPost(null);
    setImagePreview(null);
    setSelectedFiles({});
    setCurrentContent('');
    setCurrentAuthor('');
    setCurrentTags([]);
    setSelectedLanguage('tr');
    setShowPreview(false);
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Dosya boyutu kontrolü (2MB)
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Görsel dosya boyutu 2MB\'dan küçük olmalıdır');
        return;
      }

      // Dosya tipi kontrolü
      if (!file.type.startsWith('image/')) {
        toast.error('Sadece görsel dosyaları yükleyebilirsiniz');
        return;
      }

      // Eski blob URL'yi temizle
      if (imagePreview && imagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(imagePreview);
      }

      // Yeni önizleme oluştur
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
      setSelectedFiles(prev => ({ ...prev, image: file }));
    }
  };

  const handleRemoveImage = () => {
    if (imagePreview && imagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(imagePreview);
    }
    setImagePreview(null);
    setSelectedFiles(prev => ({ ...prev, image: undefined }));
  };

  const handleImageUpload = async (blogId: string) => {
    if (!selectedFiles.image) return true;
    
    try {
      console.log("Uploading blog image for ID:", blogId);
      await blogService.uploadBlogImage(blogId, selectedFiles.image);
      console.log("Blog image uploaded successfully");
      return true;
    } catch (error) {
      console.error("Error uploading blog image:", error);
      return false;
    }
  };

  const handleDelete = async (post: BlogPost) => {
    if (window.confirm('Bu blog yazısını silmek istediğinizden emin misiniz?')) {
      try {
        const postId = post.id || post._id;
        if (!postId) {
          toast.error('Blog ID bulunamadı');
          return;
        }
        await blogService.deleteBlog(postId);
        toast.success('Blog yazısı başarıyla silindi');
        fetchBlogs();
      } catch (error) {
        console.error('Error deleting blog:', error);
        toast.error('Blog yazısı silinirken hata oluştu');
      }
    }
  };

  const handleSubmit = async (formData: FormData) => {
    try {
      setSubmitting(true);
      
      const blogData = {
        slug: {
          tr: formSlugs.tr || createSlug(formTitles.tr),
          en: formSlugs.en || createSlug(formTitles.en),
          es: formSlugs.es || createSlug(formTitles.es),
        },
        title: {
          tr: formTitles.tr || '',
          en: formTitles.en || '',
          es: formTitles.es || '',
        },
        content: {
          tr: formContents.tr || '',
          en: formContents.en || '',
          es: formContents.es || '',
        },
        tags: currentTags,
        author: currentAuthor || 'Admin',
        status: formData.get('status') as string || 'draft'
      };

      let blogId;
      if (editingPost) {
        const postId = editingPost.id || editingPost._id;
        if (!postId) {
          toast.error('Blog ID bulunamadı');
          return;
        }
        await blogService.updateBlog(postId, blogData);
        blogId = postId;
        toast.success('Blog yazısı başarıyla güncellendi');
      } else {
        const result = await blogService.createBlog(blogData);
        blogId = result._id || result.id;
        toast.success('Blog yazısı başarıyla oluşturuldu');
      }

      // Görsel yükleme
      if (blogId && selectedFiles.image) {
        const uploadSuccess = await handleImageUpload(blogId);
        if (!uploadSuccess) {
          toast.error('Blog kaydedildi ancak görsel yüklenemedi');
        }
      }

      // Form'u kapat ve temizle
      handleCloseForm();
      
      // Blog listesini yenile
      fetchBlogs();
    } catch (error: any) {
      console.error('Error saving blog:', error);
      toast.error('Blog yazısı kaydedilirken hata oluştu');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const baseClasses = "px-3 py-1 text-sm font-medium rounded-full";
    switch (status) {
      case 'published':
        return `${baseClasses} bg-green-100 text-green-800`;
      case 'draft':
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  // Tag yönetimi
  const addTag = (tag: string) => {
    if (tag.trim() && !currentTags.includes(tag.trim())) {
      setCurrentTags([...currentTags, tag.trim()]);
    }
  };

  const removeTag = (tagToRemove: string) => {
    setCurrentTags(currentTags.filter(tag => tag !== tagToRemove));
  };

  // Filtreleme
  const filteredPosts = blogPosts.filter(post => {
    const matchesSearch = 
      (typeof post.title === 'string' ? post.title : (post.title?.tr || '')).toLowerCase().includes(searchTerm.toLowerCase()) ||
      (typeof post.content === 'string' ? post.content : (post.content?.tr || '')).toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return matchesSearch;
  });

  return (
    <MainLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Blog Yönetimi</h1>
            <p className="text-gray-600 mt-2">
              Blog yazılarını yönetin ve düzenleyin
            </p>
          </div>
          <button
            onClick={handleNewPost}
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium"
          >
            <Plus className="h-5 w-5 mr-2" />
            Yeni Blog Yazısı
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-3 bg-primary-100 rounded-lg">
                <FileText className="h-6 w-6 text-primary-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Toplam Blog</p>
                <p className="text-2xl font-bold text-gray-900">{blogPosts.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-3 bg-green-100 rounded-lg">
                <Eye className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Yayında</p>
                <p className="text-2xl font-bold text-gray-900">
                  {blogPosts.filter(p => p.status === 'published').length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-3 bg-yellow-100 rounded-lg">
                <Edit className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Taslak</p>
                <p className="text-2xl font-bold text-gray-900">
                  {blogPosts.filter(p => p.status === 'draft').length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="p-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <input
                  type="text"
                  placeholder="Blog yazılarında ara..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Blog Posts Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Blog Yazıları</h2>
          </div>
          
          <div>
            <table className="w-full table-fixed divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="w-16 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Görsel
                  </th>
                  <th className="w-2/5 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Başlık
                  </th>
                  <th className="w-1/5 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    İçerik
                  </th>
                  <th className="w-1/6 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Durum
                  </th>
                  <th className="w-1/6 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tarih
                  </th>
                  <th className="w-1/12 px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    İşlemler
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center">
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                        <span className="ml-2 text-gray-600">Yükleniyor...</span>
                      </div>
                    </td>
                  </tr>
                ) : filteredPosts.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                      {searchTerm ? 'Arama kriterlerine uygun blog yazısı bulunamadı.' : 'Henüz blog yazısı bulunmuyor.'}
                    </td>
                  </tr>
                ) : filteredPosts.map((post) => (
                  <tr key={post.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="h-10 w-10 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
                        {post.coverImage ? (
                          <img 
                            src={post.coverImage} 
                            alt="Cover" 
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <FileText className="h-5 w-5 text-gray-400" />
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900 truncate">
                        {typeof post.title === 'string' ? post.title : (post.title?.tr || 'İsimsiz Blog')}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-500 truncate">
                        {(() => {
                          const content = typeof post.content === 'string' ? post.content : (post.content?.tr || '');
                          const plainText = content.replace(/<[^>]*>/g, '');
                          return plainText.length > 50 ? plainText.substring(0, 50) + '...' : plainText;
                        })()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={getStatusBadge(post.status)}>
                        {post.status === 'published' ? 'Yayında' : 'Taslak'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      <div className="truncate">
                        {post.createdAt ? new Date(post.createdAt).toLocaleDateString('tr-TR') : '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleEdit(post)}
                          className="text-primary-600 hover:text-primary-900"
                          title="Düzenle"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(post)}
                          className="text-red-600 hover:text-red-900"
                          title="Sil"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Blog Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[95vh] flex flex-col">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <div className="flex items-center space-x-3">
                <FileText className="h-6 w-6 text-primary-600" />
                <h2 className="text-xl font-semibold text-gray-900">
                  {editingPost ? 'Blog Yazısı Düzenle' : 'Yeni Blog Yazısı'}
                </h2>
              </div>
              <button
                onClick={handleCloseForm}
                className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Language Tabs */}
              <div className="flex space-x-1 mb-6 bg-gray-100 p-1 rounded-lg">
                {(['tr', 'en', 'es'] as const).map((lang) => (
                  <button
                    key={lang}
                    onClick={() => setSelectedLanguage(lang)}
                    className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                      selectedLanguage === lang
                        ? 'bg-white text-primary-600 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {lang.toUpperCase()}
                  </button>
                ))}
              </div>

              <form 
                onSubmit={(e) => {
                  e.preventDefault();
                  const formData = new FormData(e.currentTarget);
                  handleSubmit(formData);
                }}
                className="space-y-6"
              >
                {/* Hidden inputs for all languages */}
                <input type="hidden" name="title_tr" value={formTitles.tr} />
                <input type="hidden" name="title_en" value={formTitles.en} />
                <input type="hidden" name="title_es" value={formTitles.es} />
                <input type="hidden" name="content_tr" value={formContents.tr} />
                <input type="hidden" name="content_en" value={formContents.en} />
                <input type="hidden" name="content_es" value={formContents.es} />

                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Başlık ({selectedLanguage.toUpperCase()})
                  </label>
                  <input
                    type="text"
                    value={formTitles[selectedLanguage] || ''}
                    onChange={(e) => {
                      const newTitle = e.target.value;
                      setFormTitles(prev => ({
                        ...prev,
                        [selectedLanguage]: newTitle
                      }));
                      
                      // Slug'ı otomatik oluştur (sadece boşsa)
                      if (!formSlugs[selectedLanguage]) {
                        setFormSlugs(prev => ({
                          ...prev,
                          [selectedLanguage]: createSlug(newTitle)
                        }));
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Blog yazısı başlığını girin"
                  />
                </div>

                {/* Slug */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Slug ({selectedLanguage.toUpperCase()})
                    <span className="text-xs text-gray-500 ml-2">
                      (URL'de kullanılacak benzersiz tanımlayıcı)
                    </span>
                  </label>
                  <input
                    type="text"
                    value={formSlugs[selectedLanguage] || ''}
                    onChange={(e) => setFormSlugs(prev => ({
                      ...prev,
                      [selectedLanguage]: e.target.value
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm"
                    placeholder="ornek-blog-slug"
                  />
                  {formTitles[selectedLanguage] && !formSlugs[selectedLanguage] && (
                    <p className="mt-1 text-xs text-gray-500">
                      Önerilen: {createSlug(formTitles[selectedLanguage])}
                    </p>
                  )}
                </div>

                {/* Content */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    İçerik ({selectedLanguage.toUpperCase()})
                  </label>
                  <div className="border border-gray-300 rounded-lg">
                    {/* Rich Text Toolbar */}
                    <div className="border-b border-gray-200 p-3 bg-gray-50">
                      <div className="flex flex-wrap items-center gap-1">
                        {/* Text Formatting */}
                        <div className="flex items-center border-r border-gray-300 pr-2 mr-2">
                          <button
                            type="button"
                            onClick={() => formatText('bold')}
                            className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded"
                            title="Kalın (Bold)"
                          >
                            <Bold className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => formatText('italic')}
                            className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded"
                            title="İtalik (Italic)"
                          >
                            <Italic className="h-4 w-4" />
                          </button>
                        </div>

                        {/* Headers */}
                        <div className="flex items-center border-r border-gray-300 pr-2 mr-2">
                          <button
                            type="button"
                            onClick={() => formatText('h1')}
                            className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded"
                            title="Başlık 1 (H1)"
                          >
                            <Heading1 className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => formatText('h2')}
                            className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded"
                            title="Başlık 2 (H2)"
                          >
                            <Heading2 className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => formatText('h3')}
                            className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded"
                            title="Başlık 3 (H3)"
                          >
                            <Heading3 className="h-4 w-4" />
                          </button>
                        </div>

                        {/* Lists */}
                        <div className="flex items-center border-r border-gray-300 pr-2 mr-2">
                          <button
                            type="button"
                            onClick={() => formatText('ul')}
                            className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded"
                            title="Madde İşaretli Liste"
                          >
                            <List className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => formatText('ol')}
                            className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded"
                            title="Numaralı Liste"
                          >
                            <ListOrdered className="h-4 w-4" />
                          </button>
                        </div>

                        {/* Other Elements */}
                        <div className="flex items-center">
                          <button
                            type="button"
                            onClick={() => formatText('blockquote')}
                            className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded"
                            title="Alıntı (Blockquote)"
                          >
                            <Quote className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => formatText('p')}
                            className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded"
                            title="Paragraf"
                          >
                            <Square className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => formatText('br')}
                            className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded"
                            title="Satır Sonu"
                          >
                            <CornerDownLeft className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => formatText('div')}
                            className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded"
                            title="Ortalanmış Div"
                          >
                            <AlignCenter className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => formatText('link')}
                            className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded"
                            title="Link Ekle"
                          >
                            <Link className="h-4 w-4" />
                          </button>
                        </div>

                        {/* Preview Toggle */}
                        <div className="ml-auto">
                          <button
                            type="button"
                            onClick={() => setShowPreview(!showPreview)}
                            className={`px-3 py-1.5 text-sm rounded ${
                              showPreview 
                                ? 'bg-primary-600 text-white' 
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}
                          >
                            {showPreview ? 'Düzenle' : 'Önizle'}
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Content Area */}
                    <div className="min-h-[400px]">
                      {showPreview ? (
                        <div className="p-4 prose max-w-none">
                          <div 
                            dangerouslySetInnerHTML={{ 
                              __html: formContents[selectedLanguage] || '<p>İçerik henüz girilmemiş...</p>' 
                            }} 
                          />
                        </div>
                      ) : (
                        <textarea
                          ref={setContentRef}
                          rows={15}
                          value={formContents[selectedLanguage] || ''}
                          onChange={(e) => {
                            const newValue = e.target.value;
                            setFormContents(prev => ({
                              ...prev,
                              [selectedLanguage]: newValue
                            }));
                            setCurrentContent(newValue);
                          }}
                          className="w-full px-3 py-3 border-0 focus:ring-0 focus:outline-none resize-none font-mono text-sm"
                          placeholder="Blog yazısının HTML içeriğini girin... Toolbar butonlarını kullanarak formatlamalar ekleyebilirsiniz."
                        />
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    HTML formatında yazabilir, toolbar butonlarını kullanabilir ve önizleme yapabilirsiniz.
                  </p>
                </div>

                {/* Cover Image */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Kapak Görseli <span className="text-red-500">*</span>
                  </label>
                  <div 
                    className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                      !imagePreview 
                        ? 'border-red-300 bg-red-50 hover:border-red-400' 
                        : 'border-gray-300 hover:border-primary-400'
                    }`}
                    onClick={() => document.getElementById('blog-image-input')?.click()}
                  >
                    {imagePreview ? (
                      <div className="relative">
                        <img
                          src={imagePreview}
                          alt="Preview"
                          className="max-h-48 mx-auto rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveImage();
                          }}
                          className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <>
                        <Upload className="h-12 w-12 text-red-400 mx-auto mb-4" />
                        <p className="text-sm text-red-600 mb-2">
                          Zorunlu: Kapak görselini yükleyin
                        </p>
                        <button
                          type="button"
                          className="inline-flex items-center px-4 py-2 border border-red-300 rounded-lg text-sm font-medium text-red-700 bg-white hover:bg-red-50"
                        >
                          <Upload className="h-4 w-4 mr-2" />
                          Görsel Seç
                        </button>
                        <p className="text-xs text-gray-500 mt-2">
                          PNG, JPG, GIF formatlarında, maksimum 2MB
                        </p>
                      </>
                    )}
                  </div>
                  <input
                    id="blog-image-input"
                    type="file"
                    accept="image/*"
                    onChange={handleImageSelect}
                    className="hidden"
                  />
                </div>

                {/* Tags */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Etiketler
                  </label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {currentTags.map((tag, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-primary-100 text-primary-800"
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={() => removeTag(tag)}
                          className="ml-2 text-primary-600 hover:text-primary-800"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                  <input
                    type="text"
                    placeholder="Etiket eklemek için yazın ve Enter'a basın"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const input = e.target as HTMLInputElement;
                        addTag(input.value);
                        input.value = '';
                      }
                    }}
                  />
                </div>

                {/* Author */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Yazar
                  </label>
                  <input
                    type="text"
                    value={currentAuthor}
                    onChange={(e) => setCurrentAuthor(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Yazar adını girin"
                  />
                </div>

                {/* Status */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Durum
                  </label>
                  <select
                    name="status"
                    defaultValue={editingPost?.status || 'draft'}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="draft">Taslak</option>
                    <option value="published">Yayında</option>
                  </select>
                </div>

                {/* Form Actions */}
                <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={handleCloseForm}
                    className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
                  >
                    İptal
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitting ? 'Kaydediliyor...' : (editingPost ? 'Güncelle' : 'Kaydet')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default Blog;